import { ORTHO_RGB_MS_M3M_PIPELINE_UUID } from "../../constants";
import slugify from "slugify";

const slugifyOptions = {
    replacement: "_",
    strict: true,
    lower: true,
};

/**
 * Get the standard local folder path of a mission on a Hiphen production PC.
 * For legacy missions that do not have renaming data type in its metadata,
 * an RGB fast ortho pipeline instance of the mission can be given to determine
 * the data type of the mission, otherwise the data type folder will just be "DATA".
 * @param {Object} mission - An HCC mission.
 * @param {Object} [pipelineInstance] - An RGB fast ortho pipeline instance of
 *  the mission.
 * @returns {string|undefined}
 */
export function getMissionLocalFolderPath(
    mission,
    pipelineInstance = undefined
) {
    let company;
    let contract;
    let site;
    let date;
    let system;

    try {
        const uploadSession = mission.metadata.uploadSession;
        company = uploadSession.contract_info.company.name;
        contract = uploadSession.contract_info.name;
        site = uploadSession.site.name;
        date = uploadSession.acquisition_date;
        system = uploadSession.contract_info.system;
    } catch (error) {
        if (error instanceof TypeError) return undefined;
        else throw error;
    }

    if (!company || !contract || !site || !date || !system) return undefined;

    company = slugify(company, slugifyOptions);
    contract = slugify(contract, slugifyOptions);
    site = slugify(site, slugifyOptions);

    let dataTypeFolder = mission.metadata?.renamingDataType?.folder;

    // If renaming data type is not available in mission metadata, we look at the pipelines in
    // the translate parameters of the pipeline instance to determine the data type
    // folder.
    if (!dataTypeFolder) {
        if (!pipelineInstance) dataTypeFolder = "DATA";
        else dataTypeFolder = getDataTypeFolder(pipelineInstance);
    }

    return `D:/PRODUCTION/${company}/${contract}/${system}/${site}/${date}/${dataTypeFolder}`;
}

/**
 * Get the data type folder by looking at the pipelines in the translate parameters
 * of the pipeline instance:
 * - if it contains the RGB fast ortho mode RGB+MS M3M pipeline ==> RGB_MS
 * - otherwise ==> RGB
 * @param {Object} pipelineInstance - A pipeline instance.
 * @returns {string}
 */
function getDataTypeFolder(pipelineInstance) {
    const pipelines = pipelineInstance.translateParams?.pipelines;

    if (pipelines?.includes(ORTHO_RGB_MS_M3M_PIPELINE_UUID)) return "RGB_MS";

    return "RGB";
}

/**
 * Get the data type folder from a mission's acquisition vector sensor bundles.
 * - RGB → "RGB", Multispectral → "MS", Thermal → "THERMAL", Lidar → "LIDAR"
 * - RGB + Multispectral → "RGB_MS", RGB + Thermal → "THERMAL", RGB + Lidar → "LIDAR"
 * - Any other combination → "DATA"
 * @param {Object[]} sensorBundles - Array of sensor bundles with eager-loaded Sensor.
 * @returns {string|undefined}
 */
function getDataTypeFolderFromSensorBundles(sensorBundles) {
    if (!sensorBundles?.length) return undefined;

    const DATA_TYPE_FOLDER_MAP = {
        RGB: "RGB",
        Multispectral: "MS",
        Thermal: "THERMAL",
        Lidar: "LIDAR",
    };

    const uniqueDataTypes = [
        ...new Set(sensorBundles.map((bundle) => bundle.Sensor.dataType)),
    ];

    if (uniqueDataTypes.length === 1) {
        return DATA_TYPE_FOLDER_MAP[uniqueDataTypes[0]] ?? "DATA";
    }

    if (uniqueDataTypes.length === 2 && uniqueDataTypes.includes("RGB")) {
        const secondDataType = uniqueDataTypes.find(
            (dataType) => dataType !== "RGB"
        );
        if (secondDataType === "Multispectral") return "RGB_MS";
        if (secondDataType === "Thermal") return DATA_TYPE_FOLDER_MAP.Thermal;
        if (secondDataType === "Lidar") return DATA_TYPE_FOLDER_MAP.Lidar;
    }

    return "DATA";
}

/**
 * Get the standard local folder path of a mission using the mission's eager-loaded
 * relations (Site, Contract, Company) and the acquisition vector's sensor bundles
 * for the data type.
 * @param {Object} mission - An HCC mission with eager-loaded relations.
 * @returns {string|undefined}
 */
export function getMissionLocalFolderPathFromRelations(mission) {
    let company = mission.Site?.Contract?.Company?.name;
    let contract = mission.Site?.Contract?.name;
    let site = mission.Site?.name;
    const date = mission.date?.slice(0, 10);

    if (!company || !contract || !site || !date) return undefined;

    company = slugify(company, slugifyOptions);
    contract = slugify(contract, slugifyOptions);
    site = slugify(site, slugifyOptions);

    const dataTypeFolder = getDataTypeFolderFromSensorBundles(
        mission.AcquisitionVector?.SensorBundles
    );
    if (!dataTypeFolder) return undefined;

    return `D:/PRODUCTION/${company}/${contract}/AV/${site}/${date}/${dataTypeFolder}`;
}
